/**
 * DocFolderGrid — the front page of the Doc Hub: the folder cards with what
 * belongs in each, and the sub-folder strip once you are inside one.
 *
 * The descriptions are the point. People were dropping everything into one
 * flat list because nothing told them where a clarification or a cost grid was
 * meant to go; the card says it in the place where they are deciding.
 *
 * Purely presentational — AttachmentsPanel owns the files and the fetches.
 */
import { useState } from 'react';
import { DOC_FOLDERS, UNFILED } from '../../data/docFolders';
import { acceptDrag } from '../../utils/droppedFiles';

const GREEN = '#1B5E20';

const CARD = {
  textAlign: 'left',
  background: '#fff',
  border: '1px solid #E2E8DD',
  borderRadius: 10,
  padding: '13px 14px',
  cursor: 'pointer',
  fontFamily: 'Montserrat',
  width: '100%',
  display: 'flex',
  gap: 11,
  alignItems: 'flex-start',
};

const CHIP = {
  padding: '4px 11px',
  borderRadius: 14,
  fontSize: 10,
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'Montserrat',
  border: '1px solid #DDE4D8',
  background: '#fff',
  color: '#5A6557',
};

/** One folder card: icon, name, what goes in it, how much is in it.
 *  Also a drop target - dragging files straight onto the right card is the
 *  whole point of having named folders. */
function FolderCard({ folder, count, onOpen, onDropFiles }) {
  const [hover, setHover] = useState(false);
  const [over, setOver] = useState(false);
  const lit = hover || over;
  return (
    <button
      type="button"
      onClick={() => onOpen(folder)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onDragOver={
        onDropFiles
          ? (e) => {
              acceptDrag(e);
              setOver(true);
            }
          : undefined
      }
      onDragLeave={onDropFiles ? () => setOver(false) : undefined}
      onDrop={
        onDropFiles
          ? (e) => {
              setOver(false);
              onDropFiles(e, folder.key);
            }
          : undefined
      }
      style={{
        ...CARD,
        borderColor: lit ? GREEN : '#E2E8DD',
        borderStyle: over ? 'dashed' : 'solid',
        background: over ? '#EDF7EC' : '#fff',
        boxShadow: lit ? '0 2px 10px rgba(27,94,32,0.10)' : '0 1px 2px rgba(0,0,0,0.03)',
      }}
    >
      <span style={{ fontSize: 17, lineHeight: 1, marginTop: 1 }} aria-hidden="true">
        {folder.icon}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <span style={{ fontSize: 11.5, fontWeight: 800, color: '#14200F' }}>{folder.label}</span>
          <span
            style={{
              fontSize: 9,
              fontWeight: 800,
              padding: '1px 7px',
              borderRadius: 9,
              color: count ? GREEN : '#A8B0A4',
              background: count ? '#E8F5E9' : '#F4F5F2',
            }}
          >
            {count}
          </span>
        </span>
        {folder.desc && (
          <span style={{ display: 'block', fontSize: 9.5, color: '#8A9289', lineHeight: 1.45, marginTop: 3 }}>{folder.desc}</span>
        )}
      </span>
    </button>
  );
}

export default function DocFolderGrid({ view = null, subFolders, countFor, onOpen, onAddSub, onDeleteSub, onDropFiles, canManage = true }) {
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [err, setErr] = useState('');

  /* ── the grid of folders ── */
  if (!view) {
    const unfiled = countFor(UNFILED.key, null);
    return (
      <div>
        <div style={{ fontSize: 9.5, color: '#8A9289', marginBottom: 10, lineHeight: 1.5 }}>
          Open a folder to see or add its files — or drag files straight onto a folder below. Photos and video are welcome.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(215px, 1fr))', gap: 9 }}>
          {DOC_FOLDERS.map((f) => (
            <FolderCard key={f.key} folder={f} count={countFor(f.key, null)} onOpen={onOpen} onDropFiles={onDropFiles} />
          ))}
          {/* Only when it holds something — a tidy job never sees this card.
              Not a drop target: "unfiled" is where things END UP, never a
              place you would deliberately put something. */}
          {unfiled > 0 && <FolderCard folder={UNFILED} count={unfiled} onOpen={onOpen} />}
        </div>
      </div>
    );
  }

  /* ── inside a folder: breadcrumb + sub-folder strip ── */
  const subs = subFolders.filter((s) => s.folder_key === view.key);
  const submit = async () => {
    const name = newName.trim();
    if (!name) return;
    const problem = await onAddSub(view.key, name);
    if (problem) {
      setErr(problem);
      return;
    }
    setNewName('');
    setAdding(false);
    setErr('');
  };

  return (
    <div style={{ marginBottom: 12 }}>
      <button
        type="button"
        onClick={() => onOpen(null)}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          fontSize: 10,
          fontWeight: 700,
          color: GREEN,
          fontFamily: 'Montserrat',
        }}
      >
        ← All folders
      </button>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '7px 0 2px' }}>
        <span style={{ fontSize: 14 }} aria-hidden="true">
          {view.icon}
        </span>
        <span style={{ fontSize: 13, fontWeight: 800, color: '#14200F' }}>{view.label}</span>
      </div>
      {view.desc && <div style={{ fontSize: 9.5, color: '#8A9289', marginBottom: 9 }}>{view.desc}</div>}

      {/* Sub-folders. Unfiled is a pseudo-folder, so it gets none. */}
      {view.key !== UNFILED.key && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 4 }}>
          <button
            type="button"
            onClick={() => onOpen(view, null)}
            style={{ ...CHIP, ...(view.subId == null ? { background: GREEN, color: '#fff', borderColor: GREEN } : {}) }}
          >
            All ({countFor(view.key, null)})
          </button>

          {subs.map((s) => (
            <span key={s.id} style={{ display: 'inline-flex', alignItems: 'center' }}>
              <button
                type="button"
                onClick={() => onOpen(view, s.id)}
                style={{
                  ...CHIP,
                  ...(view.subId === s.id ? { background: GREEN, color: '#fff', borderColor: GREEN } : {}),
                  ...(canManage ? { borderTopRightRadius: 0, borderBottomRightRadius: 0 } : {}),
                }}
              >
                {s.name} ({countFor(view.key, s.id)})
              </button>
              {canManage && (
                <button
                  type="button"
                  title={`Delete "${s.name}" — its files move back to ${view.label}`}
                  onClick={() => onDeleteSub(s)}
                  style={{
                    ...CHIP,
                    padding: '4px 7px',
                    borderLeft: 'none',
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    color: '#B4837F',
                  }}
                >
                  ×
                </button>
              )}
            </span>
          ))}

          {canManage && !adding && (
            <button
              type="button"
              onClick={() => {
                setAdding(true);
                setErr('');
              }}
              style={{ ...CHIP, borderStyle: 'dashed', color: GREEN }}
            >
              + Sub-folder
            </button>
          )}
          {canManage && adding && (
            <span style={{ display: 'inline-flex', gap: 5, alignItems: 'center' }}>
              <input
                autoFocus
                value={newName}
                onChange={(e) => {
                  setNewName(e.target.value);
                  setErr('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submit();
                  if (e.key === 'Escape') {
                    setAdding(false);
                    setNewName('');
                    setErr('');
                  }
                }}
                placeholder="Folder name"
                style={{
                  padding: '4px 9px',
                  borderRadius: 14,
                  border: '1px solid #C9D6C4',
                  fontSize: 10,
                  fontFamily: 'Montserrat',
                  outline: 'none',
                  width: 130,
                }}
              />
              {/* named: the Comments panel on the same tab also has an "Add" */}
              <button
                type="button"
                aria-label="Add sub-folder"
                onClick={submit}
                style={{ ...CHIP, background: GREEN, color: '#fff', borderColor: GREEN }}
              >
                Add
              </button>
              <button
                type="button"
                onClick={() => {
                  setAdding(false);
                  setNewName('');
                  setErr('');
                }}
                style={{ ...CHIP, color: '#9A9A9A' }}
              >
                Cancel
              </button>
            </span>
          )}
        </div>
      )}
      {err && <div style={{ fontSize: 10, fontWeight: 700, color: '#B71C1C', marginTop: 4 }}>{err}</div>}
    </div>
  );
}
